import { yupResolver } from '@hookform/resolvers/yup'
import { find, findIndex, isEqual, map } from 'lodash'
import { ChangeEvent, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { AlertRatio } from '@/components/AlertRatio'
import { PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import {
  Button,
  Card,
  Checkbox,
  DateTimePicker,
  Input,
  Modal,
  Textarea,
} from '@/components/ui'
import { cn } from '@/lib/utils'
import {
  Info,
  InfoLocalFieldsPayload,
  InfoPayload,
  Languages,
  Parent,
} from '../../../common/models'
import FormGroup from '../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../core/components/FormUploadPreview'
import { formatDateTime } from '../../../core/constants/times'
import {
  createInfo,
  deleteInfo,
  getAllInfos,
  updateInfo,
} from '../../../core/services/infos'
import { uploadImage } from '../../../core/services/upload-image'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { HtmlEditor } from '@/core/components/HtmlEditor'
import { Icon } from '@/common/components/Icon'

export interface CreateInfoModalProps {
  showCreateModal: boolean
  setShowCreateModal: (val: boolean) => void
  setTotalElements: (val: number) => void
  setInfos: (val: Info[]) => void
  setCurrentInfo: (val: Info | undefined) => void
  info?: Info
  size: number
  page: number
  parents: Parent[]
  allLanguages: Languages[]
}

export interface IInfoValues {
  ordinal?: number
  mainCategory?: string
  translations: InfoLocalFieldsPayload[]
  publishedDate?: string
  published?: boolean
  registerBuyDisplay?: boolean
  mainMenuDisplay?: boolean
  footerDisplay?: boolean
  photo?: string | null
  parent?: string
}

export const CreateInfoModal = (props: CreateInfoModalProps): JSX.Element => {
  const {
    showCreateModal,
    setShowCreateModal,
    info,
    size,
    setInfos,
    setTotalElements,
    setCurrentInfo,
    page,
    parents,
    allLanguages,
  } = props
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [showDeleteInfoModal, setShowDeleteInfoModal] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [content, setContent] = useState<InfoLocalFieldsPayload[]>([])

  const [isCustomHTMLSelected, setIsCustomHTMLSelected] = useState<boolean[]>(
    [],
  )

  useEffect(() => {
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        title: '',
        metaTitle: '',
        metaTitleUrl: '',
        metaDescription: '',
        metaKeywords: '',
        contents: '',
      }
    })
    setValue('translations', temp)
    if (!!info) {
      if (!!info.translations && info.translations.length > 0) {
        setContent(info.translations)
        setTimeout(() => {
          map(info.translations, (trans) => {
            const index = findIndex(
              allLanguages,
              (l) => l.locale === trans.locale,
            )
            if (index !== -1) {
              setValue(`translations.${index}.locale`, trans.locale)
              setValue(`translations.${index}.title`, trans.title)
              setValue(`translations.${index}.metaTitle`, trans.metaTitle)
              setValue(`translations.${index}.metaTitleUrl`, trans.metaTitleUrl)
              setValue(
                `translations.${index}.metaDescription`,
                trans.metaDescription,
              )
              setValue(`translations.${index}.metaKeywords`, trans.metaKeywords)
              setValue(`translations.${index}.contents`, trans.contents)
            }
          })
        }, 100)
      }

      setValue('ordinal', info.ordinal)
      setValue('parent', info.parent?.id)
      setValue('mainMenuDisplay', info.mainMenuDisplay)
      setValue('footerDisplay', info.footerDisplay)
      setValue('registerBuyDisplay', info.registerBuyDisplay)
      setValue(
        'publishedDate',
        info.publishedDate ? formatDateTime(info.publishedDate.valueOf()) : '',
      )
      setValue('published', info.published)
      setValue('photo', info.imageLink)
      setImage(info.imageLink)
      if (info?.imageLink) {
        const url = new URL(info.imageLink)
        const i = url?.pathname?.lastIndexOf('/') + 1
        const imageName = url?.pathname?.substring(i)
        setImageName(imageName)
      }
    }

    return () => {
      setImage('')
      setImageName('')
      reset()
    }
  }, [info, showCreateModal])

  const handleClose = () => {
    setInvalidPhoto(false)
    setIsErrorRatio(false)
    setShowCreateModal(false)
    setIsCustomHTMLSelected([])
    setImage('')
    setImageName('')
    setCurrentInfo(undefined)
  }

  const schema = yup
    .object()
    .shape({
      ordinal: yup.number(),
      mainCategory: yup.string(),
      translations: yup
        .array()
        .min(1)
        .of(
          yup
            .object()
            .shape({
              locale: yup.string().required(),
              title: yup.string().required(),
              metaTitle: yup.string().required(),
              metaTitleUrl: yup.string().required(),
              metaDescription: yup.string().required(),
              metaKeywords: yup.string().nullable().optional(),
              contents: yup.string().required(),
            })
            .required(),
        )
        .required(),
      parent: yup.string(),
      mainMenuDisplay: yup.boolean(),
      registerBuyDisplay: yup.boolean(),
      footerDisplay: yup.boolean(),
      published: yup.boolean(),
      publishedDate: yup.string(),
      photo: yup.string().nullable(),
    })
    .required()

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<IInfoValues>({
    mode: 'onChange',
    resolver: yupResolver<IInfoValues>(schema),
    defaultValues: {
      ordinal: 0,
      mainCategory: '',
      parent: 'none',
      mainMenuDisplay: false,
      registerBuyDisplay: false,
      footerDisplay: false,
      published: false,
      publishedDate: formatDateTime(new Date().toISOString()),
      translations: [],
    },
  })

  const contentWatch = watch('translations')

  const submit = async (values: IInfoValues) => {
    const parent: Parent | null =
      values?.parent === undefined || values.parent === 'none'
        ? null
        : ({
            id: values.parent,
            name:
              find(parents, (info) => info.id === values.parent)?.name || '',
          } as Parent)

    const data: InfoPayload = {
      translations: values.translations,
      ordinal: values.ordinal,
      parent: parent,
      imageLink: undefined,
      publishedDate: values.publishedDate
        ? new Date(values.publishedDate).valueOf()
        : new Date().valueOf(),
      published: values.published,
      mainMenuDisplay: values.mainMenuDisplay,
      footerDisplay: values.footerDisplay,
      registerBuyDisplay: values.registerBuyDisplay,
    }
    if (values.photo) {
      data.imageLink = values.photo
    }

    try {
      if (info) {
        const { status } = await updateInfo(info.id, data)
        const response = await getAllInfos({
          page: page - 1,
          size,
        })
        setInfos(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } else {
        const { status } = await createInfo(data)
        const response = await getAllInfos({
          page: page - 1,
          size,
        })
        setTotalElements(response.data.totalElements)
        setInfos(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
    handleClose()
  }

  const handleDelete = () => {
    setShowDeleteInfoModal(true)
  }

  const deleteInfoInModal = () => {
    // prettier-ignore
    (async () => {
      try {
        if (info) {
          const { status } = await deleteInfo([info.id])
          const response = await getAllInfos()

          setInfos(response.data.content)
          setTotalElements(response.data.totalElements)
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-5 text-2xl font-bold leading-none text-monochrome-1">
      {children}
    </div>
  )

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              icon="information"
              title="Info"
              subtitle={
                info?.translations?.[0]?.title
                  ? info.translations[0].title
                  : 'Add New Info'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            {info ? (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={
                  isSubmitting ||
                  isUploading ||
                  isErrorRatio ||
                  (Object.keys(dirtyFields).length === 0 &&
                    isEqual(contentWatch, content))
                }
                form="create-info-form"
                type="submit"
                variant="primary"
                loading={isSubmitting || isUploading}
              >
                {info?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <SectionTitle>Main Info</SectionTitle>
        <form
          id="create-info-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="flex w-full flex-col gap-2">
            {map(allLanguages, (language, index) => {
              return (
                <Card
                  className="p-4 flex flex-col space-y-4 bg-monochrome-120 border-none"
                  key={language.id}
                >
                  <FormGroup
                    label="Language"
                    labelClassName="whitespace-nowrap"
                  >
                    <Input
                      value={language.name}
                      disabled
                      className="bg-monochrome-120 border-none"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Name"
                    error={errors?.translations?.[index]?.title?.message}
                  >
                    <Input
                      isInvalid={
                        !!errors?.translations?.[index]?.title?.message
                      }
                      {...register(`translations.${index}.title`)}
                      type="text"
                      placeholder="Enter Name"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Meta Title"
                    error={errors?.translations?.[index]?.metaTitle?.message}
                  >
                    <Input
                      isInvalid={
                        !!errors?.translations?.[index]?.metaTitle?.message
                      }
                      {...register(`translations.${index}.metaTitle`)}
                      type="text"
                      placeholder="Enter Meta Title"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Meta Title URL"
                    error={errors?.translations?.[index]?.metaTitleUrl?.message}
                  >
                    <Input
                      isInvalid={
                        !!errors?.translations?.[index]?.metaTitleUrl?.message
                      }
                      {...register(`translations.${index}.metaTitleUrl`)}
                      type="text"
                      placeholder="Enter Meta Title URL"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Meta Description"
                    error={
                      errors?.translations?.[index]?.metaDescription?.message
                    }
                  >
                    <Input
                      isInvalid={
                        !!errors?.translations?.[index]?.metaDescription
                          ?.message
                      }
                      {...register(`translations.${index}.metaDescription`)}
                      type="text"
                      placeholder="Enter Meta Description"
                    />
                  </FormGroup>
                  <FormGroup
                    label="Meta Keywords"
                    error={errors?.translations?.[index]?.metaKeywords?.message}
                  >
                    <Input
                      isInvalid={
                        !!errors?.translations?.[index]?.metaKeywords?.message
                      }
                      {...register(`translations.${index}.metaKeywords`)}
                      type="text"
                      placeholder="Enter Meta Keywords"
                    />
                  </FormGroup>

                  <HtmlEditor
                    index={index}
                    label="Description"
                    name={`translations.${index}.contents`}
                    variables={[]}
                    control={control}
                    value={contentWatch?.[index]?.contents || ''}
                    onChange={(data) => {
                      setValue(`translations.${index}.contents`, data)
                    }}
                    isError={!!errors?.translations?.[index]?.contents?.message}
                  />
                </Card>
              )
            })}
          </div>

          <FormGroup label="Ordinal" error={errors?.ordinal?.message}>
            <Input
              isInvalid={!!errors?.ordinal?.message}
              {...register('ordinal')}
              type="number"
              placeholder="Enter Ordinal"
            />
          </FormGroup>

          <Controller
            name="parent"
            control={control}
            render={({ field }) => (
              <SelectField
                label="Main Category"
                placeholder="Select"
                value={field.value || 'none'}
                outline
                onValueChange={field.onChange}
                options={[
                  { value: 'none', label: 'Select', disabled: true },
                  ...map(parents, (inf) => ({
                    value: inf.id,
                    label: inf.name,
                  })),
                ]}
                error={errors?.parent?.message}
              />
            )}
          />

          <FormGroup label="Publish Date (Optional)">
            <DateTimePicker
              hasError={!!errors?.publishedDate?.message}
              {...register('publishedDate')}
              onChange={(date) => {
                register('publishedDate').onChange({
                  target: {
                    value: date ? formatDateTime(date.valueOf()) : '',
                  },
                })
                setValue(
                  'publishedDate',
                  date ? formatDateTime(date.valueOf()) : '',
                  { shouldValidate: true },
                )
              }}
              placeholder="Enter Publish Date"
            />
          </FormGroup>

          <FormGroup label="Published">
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                />
              )}
            />
          </FormGroup>

          <FormGroup label="Display">
            <div className="flex flex-col gap-2">
              <Controller
                name="mainMenuDisplay"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm text-monochrome-2">
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                    Main Menu
                  </label>
                )}
              />
              <Controller
                name="footerDisplay"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm text-monochrome-2">
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                    Footer Menu
                  </label>
                )}
              />
              <Controller
                name="registerBuyDisplay"
                control={control}
                render={({ field }) => (
                  <label className="flex items-center gap-2 text-sm text-monochrome-2">
                    <Checkbox
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                    />
                    Checkboxes for register or buying
                  </label>
                )}
              />
            </div>
          </FormGroup>

          <FormUploadPreview
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={
                  !!errors?.photo?.message || invalidPhoto || isErrorRatio
                }
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    setInvalidPhoto(false)
                    setIsErrorRatio(false)
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'INFO',
                    )
                    if (data.photoUrl) {
                      setImage(data.photoUrl)
                      setImageName(file.name)
                      setValue('photo', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={imageName}
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setImage('')
              setImageName('')
              setValue('photo', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Image (890X330)"
            previewLabel="Image (890X330)"
          />
          {isErrorRatio && <AlertRatio />}
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteInfoModal}
        setDeleteConfirmationModal={setShowDeleteInfoModal}
        deleteTitle={'Info'}
        deleteFunc={deleteInfoInModal}
        deletedItem={info?.translations?.[0]?.title}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
